import '../../Admin/admin-shared.css'
import './importDati.css'

import { useState } from 'react'

import { PageHeader } from '../../../Components/Common/PageHeader'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import Select from '../ConfigurazioneNora/Select'
import { useAvviaImport, useFermaImport, useImportDati, usePianifica } from './api'
import { classeStato, durata, FREQUENZE, numero, percentuale, quando } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/** Titolo e descrizione di un tipo di import: il testo sta qui, non nell'Executor. */
const descrizioneTipo = (tipo) =>
  ({
    rotteAeree: { titolo: t.idRotteTitolo, testo: t.idRotteTesto },
    geonames: { titolo: t.idGeoTitolo, testo: t.idGeoTesto }
  })[tipo] ?? { titolo: tipo, testo: '' }

const etichettaStato = (stato) =>
  ({
    in_corso: t.idInCorso,
    completato: t.idCompletato,
    fallito: t.idFallito,
    fermato: t.idFermato,
    interrotto: t.idInterrotto
  })[stato] ?? stato

const Stato = ({ stato }) => (
  <span className={`nbadge ${classeStato(stato)}`}>{etichettaStato(stato)}</span>
)

const Avanzamento = ({ job }) => {
  const p = percentuale(job.avanzamento)
  return (
    <div className="id-avanzamento">
      <div className="id-avanzamento__testo">
        <span>{job.fase || t.idInCorso}</span>
        <span className="id-muto">
          {job.avanzamento?.fatti != null && job.avanzamento?.totali
            ? `${numero(job.avanzamento.fatti, language)} / ${numero(job.avanzamento.totali, language)}`
            : ''}
          {' · '}
          {durata(job.iniziatoIl)}
        </span>
      </div>
      <div
        className="id-barra"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={p ?? undefined}
      >
        <div
          className={`id-barra__piena ${p == null ? 'id-barra__piena--indeterminata' : ''}`}
          style={p == null ? undefined : { width: `${p}%` }}
        />
      </div>
    </div>
  )
}

/** Una scheda per dataset: stato, ultimo import, pulsante, pianificazione. */
const SchedaTipo = ({ voce }) => {
  const { titolo, testo } = descrizioneTipo(voce.tipo)
  const avvia = useAvviaImport()
  const ferma = useFermaImport()
  const pianifica = usePianifica()
  const pianificazione = voce.pianificazione ?? { attiva: false, ogniGiorni: 7 }

  const esegui = (mutazione, argomento, riuscito) =>
    mutazione.mutate(argomento, {
      onSuccess: () => riuscito && showToast('success', riuscito),
      onError: (e) => showToast('error', t.error, e?.message)
    })

  return (
    <div className="ncard">
      <div className="ncard__head">
        <div>
          <h3 className="ncard__title">{titolo}</h3>
          <div className="ncard__sub">{testo}</div>
        </div>
        {voce.inCorso ? (
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm"
            disabled={ferma.isPending}
            onClick={() => esegui(ferma, voce.tipo, t.idFermaInviato)}
          >
            {t.idFerma}
          </button>
        ) : (
          <button
            type="button"
            className="nbtn nbtn--primary nbtn--sm"
            disabled={avvia.isPending || voce.disponibile === false}
            onClick={() => esegui(avvia, voce.tipo, t.idAvviato)}
          >
            {t.idAvviaOra}
          </button>
        )}
      </div>
      <div className="ncard__body">
        {voce.disponibile === false ? (
          <p className="id-avviso">{voce.motivoNonDisponibile || t.idNonDisponibile}</p>
        ) : null}
        {voce.inCorso ? <Avanzamento job={voce.inCorso} /> : null}
        <dl className="id-dati">
          <div>
            <dt>{t.idDataset}</dt>
            <dd>
              {voce.dataset?.righe != null
                ? `${numero(voce.dataset.righe, language)} ${t.idRighe}`
                : t.idVuoto}
            </dd>
          </div>
          <div>
            <dt>{t.idAggiornato}</dt>
            <dd>{quando(voce.dataset?.aggiornatoIl, language)}</dd>
          </div>
          <div>
            <dt>{t.idUltimoImport}</dt>
            <dd>
              {voce.ultimo ? (
                <>
                  <Stato stato={voce.ultimo.stato} /> {quando(voce.ultimo.iniziatoIl, language)}
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>{t.idProssimo}</dt>
            <dd>
              {pianificazione.attiva ? quando(pianificazione.prossimaIl, language) : t.idManuale}
            </dd>
          </div>
        </dl>
        {voce.ultimo?.messaggio && voce.ultimo.stato !== 'completato' ? (
          <p className="id-errore">{voce.ultimo.messaggio}</p>
        ) : null}
        <div className="id-pianificazione">
          <label className="id-interruttore">
            <input
              type="checkbox"
              checked={Boolean(pianificazione.attiva)}
              disabled={pianifica.isPending}
              onChange={(e) =>
                esegui(pianifica, {
                  tipo: voce.tipo,
                  attiva: e.target.checked,
                  ogniGiorni: pianificazione.ogniGiorni
                })
              }
            />
            <span>{t.idAutomatico}</span>
          </label>
          <label htmlFor={`id-freq-${voce.tipo}`} className="id-muto">
            {t.idOgni}
          </label>
          <Select
            id={`id-freq-${voce.tipo}`}
            size="sm"
            value={String(pianificazione.ogniGiorni)}
            disabled={pianifica.isPending}
            onChange={(valore) =>
              esegui(pianifica, {
                tipo: voce.tipo,
                attiva: pianificazione.attiva,
                ogniGiorni: Number(valore)
              })
            }
          >
            {[...new Set([...FREQUENZE, Number(pianificazione.ogniGiorni)])]
              .sort((a, b) => a - b)
              .map((giorni) => (
                <option key={giorni} value={String(giorni)}>
                  {giorni === 1 ? t.idGiorno : `${giorni} ${t.idGiorni}`}
                </option>
              ))}
          </Select>
        </div>
      </div>
    </div>
  )
}

const Storico = ({ storico }) => {
  const [aperto, setAperto] = useState(null)
  if (!storico?.length) return <p className="id-muto id-vuoto">{t.idNessunImport}</p>
  return (
    <div className="id-tabella-scroll">
      <table className="id-tabella">
        <thead>
          <tr>
            <th>{t.idColQuando}</th>
            <th>{t.idColDataset}</th>
            <th>{t.idColAvvio}</th>
            <th>{t.idColStato}</th>
            <th>{t.idColDurata}</th>
            <th>{t.idColRighe}</th>
          </tr>
        </thead>
        <tbody>
          {storico.map((job) => (
            <tr
              key={job.id}
              className={job.messaggio ? 'id-riga--cliccabile' : ''}
              onClick={() => job.messaggio && setAperto(aperto === job.id ? null : job.id)}
            >
              <td>{quando(job.iniziatoIl, language)}</td>
              <td>{descrizioneTipo(job.tipo).titolo}</td>
              <td>
                {job.avvio === 'pianificato' ? t.idPianificato : job.avviatoDa || t.idManuale}
              </td>
              <td>
                <Stato stato={job.stato} />
                {aperto === job.id ? <div className="id-errore">{job.messaggio}</div> : null}
              </td>
              <td>{durata(job.iniziatoIl, job.conclusoIl)}</td>
              <td>{job.righe != null ? numero(job.righe, language) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const ImportDati = () => {
  const { data, error, isLoading } = useImportDati()

  return (
    <div className="admin-page--medium">
      <PageHeader title={t.idTitolo} subtitle={t.idSottotitolo} />
      {error ? <p className="id-errore">{error.message}</p> : null}
      {isLoading ? <p className="id-muto">{t.caricamento}</p> : null}
      <div className="id-schede">
        {(data?.tipi ?? []).map((voce) => (
          <SchedaTipo key={voce.tipo} voce={voce} />
        ))}
      </div>
      <div className="ncard id-storico">
        <div className="ncard__head">
          <div>
            <h3 className="ncard__title">{t.idStorico}</h3>
            <div className="ncard__sub">{t.idStoricoSotto}</div>
          </div>
        </div>
        <div className="ncard__body ncard__body--flush">
          <Storico storico={data?.storico} />
        </div>
      </div>
    </div>
  )
}
